import { useCallback, useContext, useState, useEffect, useMemo, useRef } from 'react'
import RoleContext from '../auth/RoleContext'
import { getRequestConfig, getToken } from '../auth/auth'
import { ROLES } from '../auth/role'
import { ApiGateway } from '../config/config'
import axios from 'axios'
import { ToggleButtonGroup, ToggleButton, FormGroup, FormControlLabel, Checkbox, Autocomplete, Grid, Button, InputLabel, Select, TextField, Typography } from '@mui/material'
import FormCard from './FormCard'
import FormElement from './FormElement'
import { Delete } from '@mui/icons-material'

export default function OwnershipSelection(props = {}) {
  const roleDetails = useContext(RoleContext)

  const { 
    setValues,
    startingValues,
    isFacilityBottler,
    isCorpBottler,
    disabled,
    onLoadManufacturers = () => {},
  } = props

  const [selectionType, setSelectionType] = useState('ownership')

  const [ownershipList, setOwnershipList] = useState([])
  const [ownershipAcList, setOwnershipAcList] = useState([])
  const [manufacturerList, setManufacturerList] = useState([])
  const [manufacturerAcList, setManufacturerAcList] = useState([])
  const [distributionCenterList, setDistributionCenterList] = useState([])
  const [distributionCenterAcList, setDistributionCenterAcList] = useState([])
  const [productionCodes, setProductionCodes] = useState({})
  const [otherOwnership, setOtherOwnership] = useState(null)

  const [ownershipId, setOwnershipId] = useState(null)
  const [manufacturerId, setManufacturerId] = useState(null)
  const [distributionCenterId, setDistributionCenterId] = useState(null)
  const [supplierName, setSupplierName] = useState(null)
  const [producedHere, setProducedHere] = useState(false)

  const [loadingManufacturers, setLoadingManufacturers] = useState(false)
  const [loadingDistributionCenters, setLoadingDistributionCenters] = useState(false)
  const [loadingOwnerships, setLoadingOwnerships] = useState(false)

  const [initialized, setInitialized] = useState(false)
  const lastRequestedOwnershipIdRef = useRef(null)
  const manufacturersCacheRef = useRef({})
  const distributionCentersCacheRef = useRef(null)
  const currentLoadRequestIdRef = useRef(0)
  const manufacturerCancelSourceRef = useRef(null)
  const currentDcRequestIdRef = useRef(0)
  const distributionCancelSourceRef = useRef(null)

  const extraOwnershipOptions = useMemo(() => {
    return isFacilityBottler || isCorpBottler ? [] : [
      { ownershipId: -100, ownershipName: 'Supplier' },
      { ownershipId: -101, ownershipName: 'Distribution Center' },
    ]
  }, [isCorpBottler, isFacilityBottler])

  const notNullOrUndefined = (v) => v !== null && v !== undefined

  useEffect(() => {
    if(initialized) return
    if(startingValues === undefined) return

    setInitialized(true)
    setProducedHere(startingValues.producedHere)

    if (notNullOrUndefined(startingValues?.otherOwnership)) {
      setSelectionType('other')
      setOtherOwnership(startingValues?.otherOwnership)
    }
    if (notNullOrUndefined(startingValues?.ownershipId)) setOwnershipId(startingValues?.ownershipId)
    if (notNullOrUndefined(startingValues?.manufacturerId)) setManufacturerId(startingValues?.manufacturerId)
    if (notNullOrUndefined(startingValues?.distributionCenterId)) setDistributionCenterId(startingValues?.distributionCenterId)
    if (notNullOrUndefined(startingValues?.supplierName)) setSupplierName(startingValues?.supplierName)
  }, [
    startingValues,
    initialized
  ]);

  const ownership = useMemo(
    () => {
      if(ownershipId === null || ownershipId === undefined) return null
      return ownershipAcList.find((v) => v.ownershipId && `${v.ownershipId}` === `${ownershipId}`) || null
    },
    [ownershipAcList, ownershipId]
  );

  const manufacturer = useMemo(
    () => {
      if(manufacturerId === null || manufacturerId === undefined) return null
      return manufacturerAcList.find((v) => v.manufacturerId && `${v.manufacturerId}` === `${manufacturerId}`) || null
    },
    [manufacturerAcList, manufacturerId]
  );

  const distributionCenter = useMemo(
    () => distributionCenterAcList.find((v) => `${v.id}` === `${distributionCenterId}`) || null,
    [distributionCenterAcList, distributionCenterId]
  );

  const supplierSelected = `${ownershipId}` === '-100'
  const dcSelected = `${ownershipId}` === '-101'
  const ownershipNotSelected = supplierSelected || dcSelected
  const ownershipSelected = (ownershipId !== null && ownershipId !== undefined) && !supplierSelected && !dcSelected
  const facilitySelected = manufacturerId ? true : false
  const anyOwnershipSelected = ownershipSelected || supplierSelected || dcSelected

  useEffect(() => {
    setValues({
      manufacturerId,
      ownershipId,
      distributionCenterId,
      supplierName: supplierName,
      producedHere: producedHere,
      otherOwnership,
      selectionType
    })
  }, [
    setValues,
    otherOwnership,
    manufacturerId,
    ownershipId,
    distributionCenterId,
    supplierName,
    producedHere,
    selectionType,
  ])

  const {
    isAnyone,
    gridItemProps,
    fwGridItemProps,
    loadingExistingImLog,
  } = props

  const getProductionCode = () => {
    const code = productionCodes[manufacturer?.manufacturerId] || ""
    return code
  }

  const clearSelections = () => {
    setOwnershipId(null)
    setDistributionCenterId(null)
    setSupplierName(null)
    setManufacturerId(null)
    setProducedHere(false)
    setOtherOwnership(null)
  }

  const loadDcOptions = useCallback(async () => {
    // If we already have results cached, use them
    const cached = distributionCentersCacheRef.current
    if (Array.isArray(cached)) {
      setDistributionCenterList(cached)
      setDistributionCenterAcList(cached)
      return
    }

    // If there's an in-flight promise, await it instead of starting another request
    if (cached && typeof cached.then === 'function') {
      setLoadingDistributionCenters(true)
      try {
        const results = await cached
        setDistributionCenterList(results)
        setDistributionCenterAcList(results)
      } catch (err) {
        // ignore network errors except cancellations
        if (!axios.isCancel(err)) {
          // swallow
        }
      } finally {
        setLoadingDistributionCenters(false)
      }
      return
    }

    // No cache - start a new request and store its promise so subsequent callers reuse it
    // cancel any previous in-flight axios source for safety
    if (distributionCancelSourceRef.current) {
      try { distributionCancelSourceRef.current.cancel('new-dc-request') } catch (e) {}
      distributionCancelSourceRef.current = null
    }

    const source = axios.CancelToken.source()
    distributionCancelSourceRef.current = source

    const fetchPromise = (async () => {
      try {
        const token = await getToken()
        const basePath = `${ApiGateway.imLogs}/distribution-centers/search`
        const options = {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token.idToken}`
          },
          cancelToken: source.token
        }
        const search = {}
        if (roleDetails.hasRole([ROLES.BOTTLER, ROLES.BOTTLER_CORP])) {
          search['byMyKoId'] = true
        }
        const response = await axios.post(basePath, search, options)
        const results = response?.data?.results || []
        distributionCentersCacheRef.current = results
        return results
      } catch (err) {
        // clear cache on failure so future attempts can retry
        distributionCentersCacheRef.current = null
        throw err
      }
    })()

    // Store the in-flight promise
    distributionCentersCacheRef.current = fetchPromise
    setLoadingDistributionCenters(true)
    try {
      const results = await fetchPromise
      setDistributionCenterList(results)
      setDistributionCenterAcList(results)
    } catch (err) {
      if (!axios.isCancel(err)) {
        // ignore network errors
      }
    } finally {
      if (distributionCancelSourceRef.current === source) distributionCancelSourceRef.current = null
      setLoadingDistributionCenters(false)
    }
  }, [roleDetails])

  const loadOwnershipOptions = useCallback(async () => {
    if(selectionType === 'other') return

    const token = await getToken()

    const basePath = `${ApiGateway.ownership}/search`
    const options = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token.idToken}`
      }
    }
    const search = {
      isActive: true
    }
    if (roleDetails.hasRole([ROLES.BOTTLER, ROLES.BOTTLER_CORP])) {
      search['byMyKoId'] = true
    }
    const response = await axios.post(basePath, search, options)
    if (response.data) {
      const { results } = response.data
      setOwnershipList(results)
      if(isFacilityBottler) {
        setOwnershipAcList([...results])
      } else {
        setOwnershipAcList([...extraOwnershipOptions, ...results])
      }
    }
  }, [extraOwnershipOptions, isFacilityBottler, roleDetails, selectionType])

  const loadManufacturers = useCallback(async (_ownershipId) => {
    if (selectionType === 'other') return
    if (_ownershipId === null || _ownershipId === undefined) return

    // Return cached results if available (avoids API calls when API is restricted)
    const cached = manufacturersCacheRef.current[_ownershipId]
    if (cached) {
      setManufacturerList(cached)
      setManufacturerAcList(cached)
      const newProductionCodes = cached.reduce((s, man) => {
        s[man?.manufacturerId] = man?.productionCode
        return s
      }, {})
      setProductionCodes(newProductionCodes)
      return
    }

    const requestId = ++currentLoadRequestIdRef.current
    setLoadingManufacturers(true)
    // cancel any in-flight manufacturer request
    if (manufacturerCancelSourceRef.current) {
      try { manufacturerCancelSourceRef.current.cancel('new-request') } catch (e) {}
      manufacturerCancelSourceRef.current = null
    }
    try {
      const token = await getToken()
      const basePath = `${ApiGateway.manufacturer}/search`
      const source = axios.CancelToken.source()
      manufacturerCancelSourceRef.current = source
      const options = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.idToken}`
        },
        cancelToken: source.token
      }
      const search = { ownershipId: _ownershipId, isActive: true }
      if (roleDetails.hasRole([ROLES.BOTTLER, ROLES.BOTTLER_CORP])) search['byMyKoId'] = true
      const response = await axios.post(basePath, search, options)
      if (currentLoadRequestIdRef.current !== requestId) return // stale
      if (response && response.data) {
        const { results } = response.data
        manufacturersCacheRef.current[_ownershipId] = results
        onLoadManufacturers(_ownershipId, results)
        setManufacturerList(results)
        setManufacturerAcList(results)
        const newProductionCodes = results.reduce((s, man) => {
          s[man?.manufacturerId] = man?.productionCode
          return s
        }, {})
        setProductionCodes(newProductionCodes)
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        // ignore network errors when API is restricted; keep manufacturers empty
      }
    } finally {
      if (manufacturerCancelSourceRef.current) {
        manufacturerCancelSourceRef.current = null
      }
      if (currentLoadRequestIdRef.current === requestId) setLoadingManufacturers(false)
    }
  }, [onLoadManufacturers, roleDetails, selectionType])
  
  useEffect(() => {
    const extraOwnershipIds = extraOwnershipOptions.map(x => x.ownershipId)
    const filtered = ownershipAcList.filter(f => !extraOwnershipIds.includes(f.ownershipId))
    if(filtered.length === 1) {
      setOwnershipId(filtered[0]?.ownershipId)
    }
  }, [extraOwnershipOptions, ownershipAcList])

  

  useEffect(() => {
    (async () => {
      setLoadingOwnerships(true)
      await loadOwnershipOptions()
      setLoadingOwnerships(false)
    })()
  }, [loadOwnershipOptions, selectionType])

  useEffect(() => {
    (async () => {
      const isExtraOwnership = `${ownershipId}` === '-100' || `${ownershipId}` === '-101'

      const promises = []
      if (!isExtraOwnership && ownershipId !== null && ownershipId !== undefined) {
        if (lastRequestedOwnershipIdRef.current !== ownershipId) {
          lastRequestedOwnershipIdRef.current = ownershipId
          setLoadingManufacturers(true)
          promises.push(loadManufacturers(ownershipId))
        }
      } else {
        lastRequestedOwnershipIdRef.current = null
        // ensure no manufacturers loading when extra ownership selected
        setLoadingManufacturers(false)
      }
      if (dcSelected) promises.push(loadDcOptions())
      await Promise.all(promises)
      
      if (!isExtraOwnership) setLoadingManufacturers(false)
    })()
  }, [dcSelected, loadDcOptions, loadManufacturers, ownershipId])

  return (
    <Grid {...fwGridItemProps} item>
      <FormCard
        title={`${props.title}`}
        defaultExpanded={props.defaultExpanded}
        showProgress={false}
        summaryStyle={{ border: '.5px solid gray', borderBottomWidth: '0' }}
        detailsStyle={{ border: '.5px solid gray', borderTopWidth: '0' }}
      >
        <FormElement show={!isFacilityBottler} gridItemProps={fwGridItemProps} loading={loadingExistingImLog}>
          <ToggleButtonGroup
            disabled={props.disabled}
            color="primary"
            value={selectionType}
            exclusive
            onChange={(e) => {
              clearSelections()
              setSelectionType(e.target.value)
            }}
            aria-label="Ownership Selection"
          >
            <ToggleButton value="ownership">Select Ownership</ToggleButton>
            <ToggleButton value="other">Other</ToggleButton>
          </ToggleButtonGroup>
        </FormElement>

        {selectionType === 'ownership' && <>

          <FormElement show={isAnyone} size='small' gridItemProps={gridItemProps} loading={loadingExistingImLog || loadingOwnerships}>
            <Autocomplete
              id="ownership"
              disabled={props.disabled}
              size='small'
              getOptionLabel={(option) => option?.ownershipName || ""}
              filterOptions={(x) => x}
              options={ownershipAcList}
              autoComplete
              filterSelectedOptions
              value={ownership}
              noOptionsText="No results"
              // disabled={isCorpBottler || isFacilityBottler}
              onChange={(event, newValue) => {
                const newOwnershipId = newValue?.ownershipId
                setOwnershipId(newOwnershipId)
                // If Supplier (-100) or Distribution Center (-101) selected,
                // clear manufacturer-related state and cancel any in-flight requests
                if (`${newOwnershipId}` === '-100' || `${newOwnershipId}` === '-101') {
                  setManufacturerId(null)
                  setManufacturerList([])
                  setManufacturerAcList([])
                  setProductionCodes({})
                  try {
                    if (manufacturerCancelSourceRef.current) {
                      manufacturerCancelSourceRef.current.cancel('ownership-changed-to-extra')
                    }
                  } catch (e) {}
                }
              }}
              onInputChange={(event, newInputValue) => {
                if (!newInputValue) {
                  setOwnershipAcList([...extraOwnershipOptions, ...ownershipList])
                } else {
                  const newOwnershipList = ownershipList.filter(_own => {
                    const lowerInput = (newInputValue || "").toLowerCase();
                    const lowerItem = (_own?.ownershipName || "").toLowerCase();
                    return lowerItem.includes(lowerInput);
                  })
                  setOwnershipAcList([...extraOwnershipOptions, ...newOwnershipList])
                }
              }}
              renderInput={(params) => (
                <TextField {...params} value={params.value || ''} label="Ownership" fullWidth />
              )}
              renderOption={(props, option, i) => {
                const isLast = i?.index + 1 === ownershipAcList.length
                const isDc = option?.ownershipId === -101
                const isSupplier = option?.ownershipId === -100
                return (
                  <li {...props} >
                    <Grid paddingBottom={isDc && !isLast ? '10px' : ''} borderBottom={isDc && !isLast ? 'solid 1px gray' : ''} container alignItems="center">
                      <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                        <Typography fontWeight={isDc || isSupplier ? 'bold' : 'regular'} variant="body2" color="text.secondary">
                          {option?.ownershipName}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
            />
          </FormElement>

          {ownershipSelected && <FormElement size='small' show={isAnyone} gridItemProps={gridItemProps} loading={loadingExistingImLog || loadingManufacturers}>
            <Autocomplete
              id="manufacturer"
              disabled={isFacilityBottler || props.disabled || loadingManufacturers}
              size='small'
              getOptionLabel={(option) => `${option?.manufacturerName}` || ""}
              filterOptions={(x) => x}
              options={manufacturerAcList}
              autoComplete
              filterSelectedOptions
              value={manufacturer}
              noOptionsText="No results"
              loading={loadingManufacturers}
              onChange={(event, newValue) => setManufacturerId(newValue?.manufacturerId)}
              onInputChange={(event, newInputValue) => {
                if (!newInputValue) {
                  setManufacturerAcList(manufacturerList)
                } else {
                  const newMfrList = manufacturerList.filter(mfr => {
                    const lowerInput = (newInputValue || "").toLowerCase();
                    const lowerItem = (mfr.manufacturerName || "").toLowerCase();
                    return lowerItem.includes(lowerInput);
                  })
                  setManufacturerAcList(newMfrList)
                }
              }}
              renderInput={(params) => (
                <TextField {...params} value={params.value || ''} label="Facility" fullWidth />
              )}
              renderOption={(props, option) => {
                return (
                  <li {...props}>
                    <Grid container alignItems="center">
                      <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                        <Typography variant="body2" color="text.secondary">
                          {option?.manufacturerName}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
            />
            
          </FormElement>}

          {supplierSelected && <FormElement show={isAnyone} gridItemProps={gridItemProps} loading={loadingExistingImLog}>
            <TextField
              id="supplierName"
              disabled={props.disabled}
              size='small'
              value={supplierName || ''}
              onChange={(e) => setSupplierName(e.target.value)}
              label="Supplier Name"
              fullWidth
            />
          </FormElement>}

          {dcSelected && 
          <FormElement size='small' show={isAnyone} gridItemProps={gridItemProps} loading={loadingExistingImLog || loadingDistributionCenters}>
            <Autocomplete
              id="distributionCenter"
              disabled={props.disabled}
              size='small'
              getOptionLabel={(option) => `${option?.name}` || ""}
              filterOptions={(x) => x}
              options={distributionCenterAcList}
              autoComplete
              filterSelectedOptions
              value={distributionCenter}
              noOptionsText="No results"
              onChange={(event, newValue) => setDistributionCenterId(newValue?.id)}
              onInputChange={(event, newInputValue) => {
                if (!newInputValue) {
                  setDistributionCenterAcList(distributionCenterList)
                } else {
                  const newList = distributionCenterList.filter(x => {
                    const lowerInput = (newInputValue || "").toLowerCase();
                    const lowerItem = (x.name || "").toLowerCase();
                    return lowerItem.includes(lowerInput);
                  })
                  setDistributionCenterAcList(newList)
                }
              }}
              renderInput={(params) => (
                <TextField {...params} value={params.value || ''} label="Distribution Center" fullWidth />
              )}
              renderOption={(props, option) => {
                return (
                  <li {...props}>
                    <Grid container alignItems="center">
                      <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                        <Typography variant="body2" color="text.secondary">
                          {option?.name}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
            />
          </FormElement>}

          {facilitySelected && <FormElement show={isAnyone} gridItemProps={{ item: true, xs: 2 }} loading={loadingExistingImLog}>
            <TextField
              id="facilityCodeId"
              size='small'
              value={getProductionCode() || ''}
              label="Facility Code"
              disabled
              fullWidth
            />
          </FormElement>}

          {anyOwnershipSelected && <FormElement show={isAnyone} gridItemProps={{ item: true, xs: 2 }} loading={loadingExistingImLog}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={props.disabled}
                    checked={producedHere}
                    onChange={(e) => {
                      setProducedHere(e.target.checked)
                    }}
                  />
                }
                label="Produced Here" />
            </FormGroup>
          </FormElement>}
        </>}

        <FormElement show={selectionType === 'other'} gridItemProps={fwGridItemProps} loading={loadingExistingImLog}>
          <TextField
            id="otherOwnership"
            disabled={props.disabled}
            size='small'
            value={otherOwnership || ''}
            onChange={(event, newValue) => {
              setOtherOwnership(event.target.value)
            }}
            label="Describe Affected Ownership"
            fullWidth
          />
        </FormElement>

        <Grid item xs={12} style={{ marginTop: '4px' }}>
          {!isFacilityBottler && !props.disabled && <Button
            style={{
              position: 'absolute',
              right: '14px',
              bottom: '4px'
            }}
            onClick={props.remove}
            size="small">
            Remove Ownership
            <Delete size="small" />
          </Button>}
        </Grid>

      </FormCard>

    </Grid>

  )
}